import { useEffect, useState } from "react";

const valueOrDash = (value) => (value ? value : "—");

const normalizeLatestLocation = (trailer) => {
    const raw = trailer?.latest_update || trailer?.latest_update || trailer?.latest_location;
    if (!raw) return null;

    const timestamp =
        raw.timestamp ||
        raw.ts ||
        raw.happenedAtTime ||
        raw.ingested_at ||
        null;

    const city =
        raw.city ||
        raw?.raw?.location?.address?.city ||
        null;

    const state =
        raw.state ||
        raw?.raw?.location?.address?.state ||
        null;

    const geofenceName =
        raw?.geofence?.name ||
        raw?.geofence_name ||
        null;

    return {
        timestamp,
        city,
        state,
        geofenceName,
    };
};

const TrailerRow = ({
    trailer,
    onToggleAvailability,
    isUpdating = false,
    onSaveNotes,
    isSavingNotes = false,
}) => {
    if (!trailer) return null;

    const latestLocation = normalizeLatestLocation(trailer);

    const initialNotes = trailer.notes ?? "";
    const [notesValue, setNotesValue] = useState(initialNotes);
    const [notesDirty, setNotesDirty] = useState(false);

    useEffect(() => {
        setNotesValue(trailer.notes ?? "");
        setNotesDirty(false);
    }, [trailer.notes]);

    const handleNotesBlur = () => {
        if (!onSaveNotes || !notesDirty) return;
        const currentNotes = trailer.notes ?? "";
        if (notesValue === currentNotes) return;
        onSaveNotes(trailer, notesValue);
        setNotesDirty(false);
    };

    return (
        <tr className="trailer-row">
            <td>
                <div className="trailer-row__primary">
                    <div className="trailer-row__identity">
                        Trailer: <strong className="trailer-row__number">{valueOrDash(trailer.trailer_no)}</strong>
                    </div>
                </div>
                <div className="trailer-row__registration">
                    <div>Plate: {valueOrDash(trailer.plate_no)}</div>
                    <div>VIN: {valueOrDash(trailer.vin_no)}</div>
                </div>
            </td>
            <td>
                <div className="trailer-row__specs">
                    <div className="trailer-row__type">{valueOrDash(trailer.trailer_type)}</div>
                    <div>
                        {[trailer.year, trailer.make, trailer.model]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                    </div>
                    <div>
                        <i className="fa fa-balance-scale trailer-row__icon" aria-hidden="true" />
                        <span>{trailer.payload ? `${trailer.payload} lbs` : "—"}</span>
                    </div>
                    <div>
                        <i className="fa fa-arrows-alt trailer-row__icon" aria-hidden="true" />
                        <span>{valueOrDash(trailer.dimensions)}</span>
                    </div>
                </div>
            </td>
            <td>
                <div className="trailer-row__status">
                    <strong>{valueOrDash(trailer.status)}</strong>
                    {trailer.comments && (
                        <div className="trailer-row__comments">
                            Notes: {trailer.comments}
                        </div>
                    )}
                </div>
            </td>
            <td>
                <div className="trailer-row__locations">
                    {latestLocation ? (
                        <div className="loc-meta">
                            {/* driver */}
                            <div className="loc-line">
                                <i className="fa fa-user" aria-hidden="true" />
                                {(trailer?.assigned_drivers && trailer.assigned_drivers.length > 0) ? (
                                    trailer.assigned_drivers.map((driver) => (
                                        <span key={`trailer-driver-${driver}`} className="loc-text">
                                            {driver}
                                        </span>
                                    ))
                                ) : (
                                    <span className="loc-text">—</span>
                                )}
                            </div>
                            {/* location */}
                            <div className="loc-line">
                                <i className="fa fa-clock" aria-hidden="true" />
                                <span
                                    className="loc-text"
                                    title={latestLocation.timestamp || ""}
                                >
                                    {latestLocation.timestamp || "—"}
                                </span>
                            </div>
                            <div className="loc-line">
                                <i className="fa fa-map-marker" aria-hidden="true" />
                                <span className="loc-text">
                                    {[
                                        latestLocation.city,
                                        latestLocation.state
                                    ].filter(Boolean).join(", ") || "—"}
                                </span>
                            </div>
                            {latestLocation?.geofenceName && (
                                <div className="loc-line">
                                    <i className="fa fa-map" aria-hidden="true" />
                                    <span className="badge-geofence">
                                        {latestLocation.geofenceName?.toLowerCase()}
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <span className="trailer-row__label">Latest location —</span>
                    )}

                </div>
            </td>
            <td className="trailer-row__notes">
                <textarea
                    className="trailer-row__notes-input"
                    value={notesValue}
                    onChange={(event) => {
                        setNotesValue(event.target.value);
                        setNotesDirty(true);
                    }}
                    onBlur={handleNotesBlur}
                    placeholder="Add notes..."
                    rows={3}
                    disabled={isSavingNotes}
                />
                <small className="trailer-row__notes-hint">
                    {isSavingNotes
                        ? "Saving..."
                        : notesDirty
                            ? "Click away to save"
                            : "Last synced notes"}
                </small>
                <button
                    type="button"
                    className="trailer-row__action-btn"
                    disabled={!onToggleAvailability || isUpdating}
                    onClick={() => onToggleAvailability && onToggleAvailability(trailer)}
                >
                    {isUpdating
                        ? "Saving..."
                        : trailer.available
                            ? "Mark Unavailable"
                            : "Mark Available"}
                </button>
            </td>
        </tr>
    );
};

export default TrailerRow;
